import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, Summary, SearchBar, EmptyState, Fab, Sheet,
  ActionRow, DetailRow, Field, TextInput, TextAreaInput, Mono, Ico, fmt,
  useMobileMenu,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import {
  deleteExpenseCategory,
  fetchExpenseCategories,
  saveExpenseCategory,
} from "./expensesMobileApi";

const DEFAULT_PERMISSIONS = {
  VIEW: "Gestion Commerciale.Depenses.Categories.view",
  EDIT: "Gestion Commerciale.Depenses.Categories.edit",
  DELETE: "Gestion Commerciale.Depenses.Categories.delete",
};

export default function ExpenseCategoriesMobile({
  moduleScope = "commercial",
  permissions = DEFAULT_PERMISSIONS,
  title = "Catégories de dépenses",
  onNavigateSibling,
}) {
  const onMenu = useMobileMenu();
  const navigate = useNavigate();
  const { selectedCompany } = useSelection();
  const { hasPermission, isLoading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const inFlight = useRef(false);

  const canEdit = hasPermission(permissions.EDIT);
  const canDelete = hasPermission(permissions.DELETE);

  const load = useCallback(async () => {
    if (!selectedCompany || !hasPermission(permissions.VIEW)) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      setRows(await fetchExpenseCategories(selectedCompany, moduleScope));
    } catch (error) {
      message.error(error?.response?.data?.error || "Impossible de charger les catégories");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, moduleScope, permissions.VIEW, selectedCompany]);

  useEffect(() => { if (!authLoading) load(); }, [authLoading, load]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => `${row.name || ""} ${row.description || ""}`.toLowerCase().includes(needle));
  }, [query, rows]);

  const openForm = (row = null) => {
    setEditing(row);
    setForm({ name: row?.name || "", description: row?.description || "" });
    setFormOpen(true);
    setDetail(null);
  };

  const submit = async () => {
    if (inFlight.current || !form.name.trim() || !selectedCompany) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await saveExpenseCategory(editing?.id, {
        name: form.name.trim(),
        description: form.description.trim(),
        company_id: selectedCompany,
        module_scope: moduleScope,
      });
      message.success(editing ? "Catégorie mise à jour" : "Catégorie créée");
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      message.error(error?.response?.data?.error || error?.message || "Impossible d'enregistrer la catégorie");
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  };

  const remove = (row) => Modal.confirm({
    title: "Supprimer cette catégorie ?",
    content: row.name,
    okText: "Supprimer",
    okType: "danger",
    cancelText: "Annuler",
    centered: true,
    onOk: async () => {
      try {
        await deleteExpenseCategory(row.id, moduleScope);
        message.success("Catégorie supprimée");
        setDetail(null);
        load();
      } catch (error) {
        message.error(error?.response?.data?.error || error?.message || "Suppression impossible");
      }
    },
  });

  if (authLoading) return <ProductScreen><Appbar title={title} onMenu={onMenu} /><div className="body"><EmptyState loading title="Chargement…" /></div></ProductScreen>;
  if (!hasPermission(permissions.VIEW)) return <ProductScreen><Appbar title={title} onMenu={onMenu} /><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" text="Vous n'avez pas la permission d'afficher ces catégories." /></div></ProductScreen>;
  if (!selectedCompany) return <ProductScreen><Appbar title={title} onMenu={onMenu} /><div className="body"><EmptyState icon="store" title="Entreprise requise" text="Sélectionnez une entreprise." /></div></ProductScreen>;

  return (
    <ProductScreen>
      <Appbar
        title={title}
        subtitle={`${fmt(rows.length)} catégorie${rows.length > 1 ? "s" : ""}`}
        onMenu={onMenu}
        action={<button className="iconbtn" aria-label="Changer de rubrique" onClick={() => setNavOpen(true)}><Ico name="list-ordered" /></button>}
      />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: "Catégories", value: fmt(rows.length) }, { label: "Avec description", value: fmt(rows.filter((r) => r.description).length) }]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Rechercher une catégorie…" />
      </div>
      <div className="body">
        {loading && !rows.length ? <EmptyState loading title="Chargement…" /> : !visible.length ? (
          <EmptyState icon="tag" title="Aucune catégorie" text={query ? "Aucun résultat pour cette recherche." : "Créez votre première catégorie de dépense."} />
        ) : (
          <div className="list">
            {visible.map((row) => (
              <button key={row.id} className="rcard" onClick={() => setDetail(row)}>
                <Mono name={row.name} square />
                <div className="rcard__main"><div className="rcard__name">{row.name}</div><div className="rcard__sub">{row.description || "Sans description"}</div></div>
                <span className="rcard__chev"><Ico name="chevron-right" /></span>
              </button>
            ))}
          </div>
        )}
      </div>
      {canEdit ? <Fab label="Nouvelle catégorie" onClick={() => openForm()} /> : null}

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…">
        <ActionRow icon="chevron-right" label="Saisie des dépenses" onClick={() => onNavigateSibling ? onNavigateSibling("entries") : navigate("/gestion-charges-admin/saisie")} />
      </Sheet>
      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.name}>
        <DetailRow label="Nom" value={detail?.name || "—"} />
        <DetailRow label="Description" value={detail?.description || "—"} />
        {canEdit ? <ActionRow icon="pencil" label="Modifier" onClick={() => openForm(detail)} /> : null}
        {canDelete ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}
      </Sheet>
      <Sheet open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Modifier la catégorie" : "Nouvelle catégorie"}>
        <Field label="Nom *"><TextInput value={form.name} onChange={(name) => setForm((f) => ({ ...f, name }))} placeholder="Ex : Fournitures" autoFocus /></Field>
        <Field label="Description"><TextAreaInput value={form.description} onChange={(description) => setForm((f) => ({ ...f, description }))} rows={4} /></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submit} disabled={saving || !form.name.trim()}>{saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>
    </ProductScreen>
  );
}
